import React, { useEffect, useMemo, useState, useCallback } from "react";
import { formatCHF } from "../utils/formatCHF";
import {
  getOrderStatusLabel,
  getOrderPaymentNotice,
  normalizeOrderTotalCents,
} from "../utils/orderPayment";

/**
 * ======================================================================
 * CONFIG API
 * ======================================================================
 */
const API_URL =
  "https://marchedeluxe.ch/services/get_orders_unpaid_magasin.php";

const DELETE_ORDER_API = "https://marchedeluxe.ch/services/delete_order.php";

/* ======================================================================
 * HELPERS
 * ====================================================================== */

const formatOrderDate = (raw) => {
  if (!raw) return "—";

  const iso =
    typeof raw === "string" && raw.includes(" ") ? raw.replace(" ", "T") : raw;

  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(raw);

  return d.toLocaleString("fr-CH", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const deliveryLabel = (mode) =>
  mode === "livraison" ? "📦 Livraison" : "🛍️ Pick & Pay";

const parseItems = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;

  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  return [];
};

const getClientDisplayName = (order) => {
  const customerType = String(order?.customer_type || "").toUpperCase();

  if (customerType === "GUEST") {
    const guestName = String(order?.guest_name || "").trim();
    if (guestName) return guestName;

    const guestLabel = String(order?.guest_label || "").trim();
    if (guestLabel) return guestLabel;

    return "Visiteur";
  }

  const prenom = String(order?.client_prenom || "").trim();
  const nom = String(order?.client_nom || "").trim();
  const full = `${prenom} ${nom}`.trim();

  if (full) return full;
  if (order?.client_email) return order.client_email;

  return "Client";
};

const getClientPhone = (order) => {
  return (
    order?.guest_phone ||
    order?.client_phone ||
    order?.client_tel ||
    order?.client_telephone ||
    order?.telephone ||
    order?.phone ||
    order?.tel ||
    ""
  );
};

const getItemQty = (item) =>
  Number(item?.qty ?? item?.quantite ?? item?.quantity ?? 1) || 1;

const getItemLabel = (item, idx) =>
  item?.product_name ||
  item?.name ||
  item?.nom ||
  item?.title ||
  `Produit #${item?.id ?? idx + 1}`;

const getItemUnitPriceCents = (item) => {
  const raw = Number(
    item?.appliedPrice ?? item?.price ?? item?.prix ?? item?.unit_price ?? 0
  );

  if (!Number.isFinite(raw) || raw <= 0) return 0;

  return raw < 100 ? Math.round(raw * 100) : Math.round(raw);
};

/* ======================================================================
 * COMPOSANT
 * ====================================================================== */

const UnpaidOrdersModal = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [orders, setOrders] = useState([]);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [deletingOrderId, setDeletingOrderId] = useState(null);

  useEffect(() => {
    if (!isOpen) return;

    const onKey = (e) => {
      if (e.key === "Escape") {
        onClose?.();
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setErrorMsg("");

    try {
      const token = localStorage.getItem("user_token") || "";

      const res = await fetch(`${API_URL}?limit=200`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json().catch(() => ({}));

      if (res.status === 401) {
        setErrorMsg("Session expirée. Merci de vous reconnecter.");
        return;
      }

      if (!res.ok || !data?.success) {
        setErrorMsg(
          data?.message || "Impossible de charger les commandes non payées."
        );
        return;
      }

      setOrders(Array.isArray(data.commandes) ? data.commandes : []);
    } catch (err) {
      setErrorMsg(err?.message || "Erreur réseau lors du chargement.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    fetchOrders();
  }, [isOpen, fetchOrders]);

  const normalizedOrders = useMemo(() => {
    return (Array.isArray(orders) ? orders : []).map((order) => {
      const items = parseItems(order?.items);
      const totalCents = normalizeOrderTotalCents(order?.total_ttc, items);

      return {
        ...order,
        _items: items,
        _totalCents: totalCents,
        _client: getClientDisplayName(order),
      };
    });
  }, [orders]);

  const toggleExpanded = useCallback((orderId) => {
    setExpandedOrderId((prev) => (prev === orderId ? null : orderId));
  }, []);

  const handleDeleteOrderAsStaff = useCallback(async (orderId) => {
    if (!orderId) return;

    const confirmed = window.confirm(
      `Voulez-vous vraiment supprimer la commande #${orderId} ?`
    );
    if (!confirmed) return;

    try {
      setDeletingOrderId(orderId);
      setErrorMsg("");

      const token = localStorage.getItem("user_token") || "";

      const res = await fetch(DELETE_ORDER_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ order_id: Number(orderId) }),
      });

      const data = await res.json().catch(() => ({}));

      if (res.status === 401) {
        setErrorMsg("Session expirée. Merci de vous reconnecter.");
        return;
      }

      if (!res.ok || !data?.success) {
        throw new Error(
          data?.message || "Impossible de supprimer cette commande."
        );
      }

      setOrders((prev) => prev.filter((o) => Number(o.id) !== Number(orderId)));
      setExpandedOrderId((prev) => (prev === orderId ? null : prev));
    } catch (err) {
      setErrorMsg(err?.message || "Erreur lors de la suppression.");
    } finally {
      setDeletingOrderId(null);
    }
  }, []);

  if (!isOpen) return null;

  return (
    <div
      className="mdx-unpaid-backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Commandes non payées"
    >
      <style>{`
        .mdx-unpaid-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,.42);
          display: flex;
          align-items: flex-start;
          justify-content: center;
          padding: 16px;
          z-index: 10000;
        }

        .mdx-unpaid-modal {
          width: min(1240px, 100%);
          max-height: calc(100vh - 32px);
          background: #fff;
          border-radius: 18px;
          border: 1px solid rgba(0,0,0,.10);
          box-shadow: 0 24px 60px rgba(0,0,0,.25);
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .mdx-unpaid-head {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
          padding: 16px 18px;
          border-bottom: 1px solid rgba(0,0,0,.06);
          background: rgba(0,0,0,.02);
        }

        .mdx-unpaid-title {
          margin: 0;
          font-size: 16px;
          letter-spacing: .02em;
        }

        .mdx-unpaid-sub {
          margin-top: 4px;
          font-size: 12px;
          opacity: .72;
          line-height: 1.45;
        }

        .mdx-unpaid-close {
          border: 1px solid rgba(0,0,0,.12);
          background: rgba(255,255,255,.88);
          border-radius: 10px;
          padding: 8px 12px;
          cursor: pointer;
          flex-shrink: 0;
        }

        .mdx-unpaid-body {
          overflow: auto;
          padding: 16px;
          display: grid;
          gap: 16px;
          background: #fafafa;
        }

        .mdx-unpaid-loading,
        .mdx-unpaid-empty,
        .mdx-unpaid-error {
          padding: 16px;
          border-radius: 14px;
          background: #fff;
          border: 1px solid rgba(0,0,0,.08);
        }

        .mdx-unpaid-error {
          border-color: rgba(176, 0, 32, .18);
          background: rgba(176, 0, 32, .04);
          color: #8f1730;
        }

        .mdx-unpaid-card {
          background: #fff;
          border: 1px solid rgba(0,0,0,.08);
          border-radius: 16px;
          overflow: visible;
          box-shadow: 0 8px 24px rgba(0,0,0,.05);
        }

        .mdx-unpaid-card-head {
          display: flex;
          justify-content: space-between;
          gap: 18px;
          padding: 16px 18px;
          border-bottom: 1px solid rgba(0,0,0,.06);
          background: rgba(255,248,225,.65);
        }

        .mdx-unpaid-head-left {
          min-width: 0;
          flex: 1 1 auto;
        }

        .mdx-unpaid-head-right {
          flex: 0 0 auto;
          min-width: 220px;
          text-align: right;
        }

        .mdx-unpaid-ref {
          font-weight: 800;
          font-size: 16px;
          line-height: 1.3;
        }

        .mdx-unpaid-meta {
          margin-top: 6px;
          font-size: 12px;
          opacity: .75;
          line-height: 1.45;
        }

        .mdx-unpaid-badge {
          display: inline-flex;
          align-items: center;
          padding: 6px 10px;
          border-radius: 999px;
          font-size: 12px;
          border: 1px solid rgba(212,175,55,.35);
          background: rgba(255,248,225,.85);
          color: #8b4513;
          margin-top: 10px;
        }

        .mdx-unpaid-total-label {
          font-size: 12px;
          opacity: .7;
        }

        .mdx-unpaid-total-amount {
          font-size: 22px;
          font-weight: 800;
          margin-top: 4px;
          line-height: 1.2;
        }

        .mdx-unpaid-top-actions {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          flex-wrap: wrap;
          margin-top: 12px;
        }

        .mdx-unpaid-btn {
          border: 1px solid rgba(0,0,0,.12);
          background: rgba(255,255,255,.92);
          border-radius: 10px;
          padding: 10px 12px;
          font-size: 13px;
          cursor: pointer;
          min-height: 40px;
        }

        .mdx-unpaid-btn-primary {
          border-color: rgba(0,0,0,.16);
          background: rgba(0,0,0,.03);
        }

        .mdx-unpaid-btn-danger {
          border-color: rgba(176, 0, 32, .18);
          background: rgba(176, 0, 32, .05);
          color: #8f1730;
        }

        .mdx-unpaid-btn:disabled {
          opacity: .5;
          cursor: not-allowed;
        }

        .mdx-unpaid-notice {
          margin: 14px 18px 0;
          padding: 12px 14px;
          border-radius: 12px;
          border: 1px solid rgba(212,175,55,.35);
          background: rgba(255,248,225,.9);
          color: #8b4513;
          line-height: 1.5;
          font-size: 13px;
        }

        .mdx-unpaid-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 12px;
          padding: 16px 18px;
        }

        .mdx-unpaid-panel {
          border: 1px solid rgba(0,0,0,.08);
          border-radius: 12px;
          padding: 12px;
          background: #fff;
          min-width: 0;
        }

        .mdx-unpaid-k {
          font-size: 12px;
          opacity: .7;
        }

        .mdx-unpaid-v {
          margin-top: 4px;
          font-weight: 700;
          word-break: break-word;
          line-height: 1.45;
        }

        .mdx-unpaid-full {
          grid-column: 1 / -1;
        }

        .mdx-unpaid-items {
          padding: 0 18px 18px;
        }

        .mdx-unpaid-items-title {
          font-size: 13px;
          font-weight: 800;
          margin-bottom: 10px;
          text-transform: uppercase;
          letter-spacing: .03em;
        }

        .mdx-unpaid-item {
          display: flex;
          justify-content: space-between;
          gap: 14px;
          padding: 10px 12px;
          border-radius: 10px;
          background: rgba(0,0,0,.03);
          margin-bottom: 8px;
        }

        .mdx-unpaid-item-left {
          min-width: 0;
          line-height: 1.45;
        }

        .mdx-unpaid-item-sub {
          margin-top: 4px;
          font-size: 12px;
          opacity: .72;
        }

        .mdx-unpaid-item-right {
          white-space: nowrap;
          font-weight: 700;
          flex-shrink: 0;
        }

        .mdx-unpaid-bottom-actions {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          padding: 0 18px 18px;
          flex-wrap: wrap;
        }

        @media (max-width: 900px) {
          .mdx-unpaid-card-head {
            flex-direction: column;
          }

          .mdx-unpaid-head-right {
            min-width: 0;
            width: 100%;
            text-align: left;
          }

          .mdx-unpaid-top-actions {
            justify-content: flex-start;
          }

          .mdx-unpaid-grid {
            grid-template-columns: 1fr;
          }

          .mdx-unpaid-item {
            flex-direction: column;
          }

          .mdx-unpaid-item-right {
            white-space: normal;
          }

          .mdx-unpaid-bottom-actions {
            justify-content: flex-start;
          }
        }

        @media (max-width: 640px) {
          .mdx-unpaid-backdrop {
            padding: 0;
          }

          .mdx-unpaid-modal {
            width: 100%;
            max-height: 100vh;
            height: 100vh;
            border-radius: 0;
            border: none;
          }

          .mdx-unpaid-head {
            padding: 14px;
          }

          .mdx-unpaid-body {
            padding: 12px;
            gap: 12px;
          }

          .mdx-unpaid-card-head {
            padding: 14px;
          }

          .mdx-unpaid-notice {
            margin: 12px 14px 0;
          }

          .mdx-unpaid-grid {
            padding: 14px;
          }

          .mdx-unpaid-items {
            padding: 0 14px 14px;
          }

          .mdx-unpaid-bottom-actions {
            padding: 0 14px 14px;
          }

          .mdx-unpaid-top-actions {
            flex-direction: column;
            align-items: stretch;
          }

          .mdx-unpaid-bottom-actions {
            flex-direction: column;
            align-items: stretch;
          }

          .mdx-unpaid-btn {
            width: 100%;
          }
        }
      `}</style>

      <div className="mdx-unpaid-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mdx-unpaid-head">
          <div>
            <h2 className="mdx-unpaid-title">Commandes non payées</h2>
            <div className="mdx-unpaid-sub">
              Vue séparée pour consultation magasin. Ces commandes ne doivent
              pas être préparées tant que le paiement n’est pas confirmé.
            </div>
          </div>

          <button
            type="button"
            className="mdx-unpaid-close"
            onClick={onClose}
            title="Fermer"
          >
            ✕ Fermer
          </button>
        </div>

        <div className="mdx-unpaid-body">
          {loading && (
            <div className="mdx-unpaid-loading">
              Chargement des commandes non payées…
            </div>
          )}

          {!loading && errorMsg && (
            <div className="mdx-unpaid-error">
              <strong>Erreur :</strong> {errorMsg}
            </div>
          )}

          {!loading && !errorMsg && normalizedOrders.length === 0 && (
            <div className="mdx-unpaid-empty">
              Aucune commande non payée à afficher.
            </div>
          )}

          {!loading &&
            !errorMsg &&
            normalizedOrders.map((order) => {
              const customerType = String(
                order?.customer_type || ""
              ).toUpperCase();
              const clientName = getClientDisplayName(order);
              const phone = getClientPhone(order);
              const items = order?._items || [];
              const isExpanded = expandedOrderId === order?.id;

              return (
                <div key={order?.id} className="mdx-unpaid-card">
                  <div className="mdx-unpaid-card-head">
                    <div className="mdx-unpaid-head-left">
                      <div className="mdx-unpaid-ref">
                        Commande #{order?.id}
                      </div>

                      <div className="mdx-unpaid-meta">
                        {formatOrderDate(order?.created_at)}
                        {" • "}
                        {deliveryLabel(order?.delivery_mode)}
                        {" • "}
                        {customerType === "GUEST" ? "VISITEUR" : "CONNECTÉ"}
                      </div>

                      <div className="mdx-unpaid-badge">
                        {getOrderStatusLabel(order?.status)}
                      </div>
                    </div>

                    <div className="mdx-unpaid-head-right">
                      <div className="mdx-unpaid-total-label">Montant</div>
                      <div className="mdx-unpaid-total-amount">
                        {formatCHF(order?._totalCents)}
                      </div>

                      <div className="mdx-unpaid-top-actions">
                        <button
                          type="button"
                          className="mdx-unpaid-btn mdx-unpaid-btn-primary"
                          onClick={() => toggleExpanded(order?.id)}
                        >
                          {isExpanded
                            ? "Masquer la commande"
                            : "Afficher la commande"}
                        </button>
                      </div>
                    </div>
                  </div>

                  {isExpanded && (
                    <>
                      <div className="mdx-unpaid-notice">
                        <strong>Paiement non confirmé :</strong>{" "}
                        {getOrderPaymentNotice(order?.status)}
                      </div>

                      <div className="mdx-unpaid-grid">
                        <div className="mdx-unpaid-panel">
                          <div className="mdx-unpaid-k">Client</div>
                          <div className="mdx-unpaid-v">
                            {clientName || "—"}
                          </div>
                        </div>

                        <div className="mdx-unpaid-panel">
                          <div className="mdx-unpaid-k">Email</div>
                          <div className="mdx-unpaid-v">
                            {order?.guest_email || order?.client_email || "—"}
                          </div>
                        </div>

                        <div className="mdx-unpaid-panel">
                          <div className="mdx-unpaid-k">Téléphone</div>
                          <div className="mdx-unpaid-v">{phone || "—"}</div>
                        </div>

                        <div className="mdx-unpaid-panel">
                          <div className="mdx-unpaid-k">Type client</div>
                          <div className="mdx-unpaid-v">
                            {customerType === "GUEST" ? "Visiteur" : "Connecté"}
                            {customerType === "GUEST" && order?.guest_label
                              ? ` • ${order.guest_label}`
                              : ""}
                          </div>
                        </div>

                        <div className="mdx-unpaid-panel">
                          <div className="mdx-unpaid-k">
                            Mode de récupération
                          </div>
                          <div className="mdx-unpaid-v">
                            {deliveryLabel(order?.delivery_mode)}
                          </div>
                        </div>

                        <div className="mdx-unpaid-panel">
                          <div className="mdx-unpaid-k">Créneau Pick & Pay</div>
                          <div className="mdx-unpaid-v">
                            {order?.horaire_pickpay?.trim()
                              ? order.horaire_pickpay
                              : "—"}
                          </div>
                        </div>

                        <div className="mdx-unpaid-panel mdx-unpaid-full">
                          <div className="mdx-unpaid-k">
                            Notes / instructions
                          </div>
                          <div className="mdx-unpaid-v">
                            {order?.notes?.trim() ? order.notes : "—"}
                          </div>
                        </div>
                      </div>

                      <div className="mdx-unpaid-items">
                        <div className="mdx-unpaid-items-title">Articles</div>

                        {items.length > 0 ? (
                          items.map((item, idx) => {
                            const qty = getItemQty(item);
                            const label = getItemLabel(item, idx);
                            const unitPriceCents = getItemUnitPriceCents(item);
                            const lineTotalCents = unitPriceCents * qty;

                            return (
                              <div
                                key={`${order?.id}-${idx}`}
                                className="mdx-unpaid-item"
                              >
                                <div className="mdx-unpaid-item-left">
                                  <strong>{qty}×</strong> {label}
                                  {Number(item?.grams || 0) > 0
                                    ? ` — ${item.grams}g`
                                    : ""}
                                  {item?.variante ? ` — ${item.variante}` : ""}
                                  {/* 🔥 DETAILS PLATEAU */}
                                  {item?.details_label && (
                                    <div className="mdx-unpaid-item-sub">
                                      {item.details_label}
                                    </div>
                                  )}
                                  <div className="mdx-unpaid-item-sub">
                                    Prix unitaire : {formatCHF(unitPriceCents)}
                                  </div>
                                </div>

                                <div className="mdx-unpaid-item-right">
                                  {formatCHF(lineTotalCents)}
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <div className="mdx-unpaid-panel">
                            Panier indisponible.
                          </div>
                        )}
                      </div>

                      <div className="mdx-unpaid-bottom-actions">
                        <button
                          type="button"
                          className="mdx-unpaid-btn mdx-unpaid-btn-danger"
                          onClick={() => handleDeleteOrderAsStaff(order?.id)}
                          disabled={deletingOrderId === order?.id}
                        >
                          {deletingOrderId === order?.id
                            ? "Suppression..."
                            : "Supprimer la commande"}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default UnpaidOrdersModal;
